import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { PurchaseOrderDetail } from '@/lib/api';
import { ClipboardList, Eye, Archive, Loader2 } from 'lucide-react';
import clsx from 'clsx';

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold',
        status === 'DRAFT' && 'bg-risk-yellow-soft text-risk-yellow',
        status === 'ORDERED' && 'bg-brand-soft text-brand-deep',
        status === 'ARCHIVED' && 'bg-surface-2 text-ink-soft'
      )}
    >
      {status === 'DRAFT' ? 'Entwurf' : status === 'ORDERED' ? 'Bestellt' : 'Archiviert'}
    </span>
  );
}

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<PurchaseOrderDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const loadData = async () => {
    try {
      const data = await api.getPurchaseOrders();
      setOrders(data);
    } catch (err) {
      console.error('Fehler beim Laden der Bestelllisten:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleArchive = async (id: string) => {
    if (!confirm('Archivierte Listen können nicht mehr bearbeitet werden.')) return;
    setArchivingId(id);
    try {
      await api.updatePurchaseOrderStatus(id, 'archive');
      await loadData();
    } catch (err) {
      console.error('Archivierung fehlgeschlagen:', err);
    } finally {
      setArchivingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ClipboardList className="h-8 w-8 text-brand" />
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink">Bestelllisten</h1>
          <p className="font-mono text-[11px] text-ink-soft mt-0.5">
            Übersicht aller generierten Bestelllisten
          </p>
        </div>
      </div>

      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Alle Listen</h2>
          <span className="font-mono text-[11px] text-ink-soft">{orders.length} Einträge</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-3 text-left">
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Erstellt am</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft text-right">Artikel</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Status</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Erstellt von</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-ink-soft font-mono text-[12px]">
                    Noch keine Bestelllisten vorhanden. Erstelle eine über die Lagerrisiken-Seite.
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr
                    key={order.id}
                    onDoubleClick={() => navigate(`/bestelllisten/${order.id}`)}
                    className="border-b border-surface-3 hover:bg-surface-2 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 font-mono text-[12.5px] text-ink">{formatDate(order.createdAt)}</td>
                    <td className="px-4 py-3 font-mono text-[12.5px] text-ink text-right">{order.itemCount}</td>
                    <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                    <td className="px-4 py-3 text-[13.5px] text-ink">{order.generatedByName}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/bestelllisten/${order.id}`)}
                          className="text-xs font-bold text-brand-deep bg-brand-soft px-3.5 py-1.5 rounded-full hover:bg-brand-100 flex items-center gap-1 transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Ansicht
                        </button>
                        {(order.status === 'DRAFT' || order.status === 'ORDERED') && (
                          <button
                            onClick={() => handleArchive(order.id)}
                            disabled={archivingId === order.id}
                            className="text-xs font-bold text-ink-muted bg-surface-2 px-3.5 py-1.5 rounded-full hover:text-ink flex items-center gap-1 transition-colors"
                          >
                            {archivingId === order.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Archive className="h-3.5 w-3.5" />
                            )}
                            Archivieren
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
